import React, {
    Fragment,
    Suspense,
    useCallback,
    useEffect,
    useMemo,
    useRef
} from "react";

import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import type { ExtensionHandlers } from "@atlaskit/editor-common/extensions";
import { type RendererProps } from "@atlaskit/renderer";
import { token } from "@atlaskit/tokens";

import { Button } from "@/libraries/design/components/button/src/button";
import { Link } from "@/libraries/design/components/link/src/link";
import { wacToken } from "@/libraries/design/foundations";

import { Source } from "../ai-summary";
import { useAISummaryContext } from "../ai-summary-context";

import { encodeAdf } from "./encoder";

const AkReactRenderer = React.lazy(() =>
    import("@atlaskit/renderer").then(({ ReactRenderer }) => ({
        default: ReactRenderer
    }))
);

export type MarkdownRendererProps = {
    answerString: string;
    statusComponent: (props: {
        text?: string;
        sources?: Source[];
    }) => React.ReactNode;
    query?: string;
    isSourceFiltered?: boolean;
};

const MediaComponent = () => <Fragment />;
const MediaSingleComponent = () => <Fragment />;

const buttonWrapperStyle = css({
    display: "inline-block",
    marginBlockStart: token("space.100"),
    marginInlineEnd: token("space.100")
});

const rovoButtonWrapperStyle = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    marginBlockStart: token("space.200")
});

const rovoButtonStyle = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    width: "100%",
    maxWidth: "none",
    height: "20px"
});

const HIGHLIGHT_NAME = "wac-citation-highlight";

/**
 * Checks whether the CSS Custom Highlight API is available in the browser.
 */
function supportsHighlightAPI(): boolean {
    return (
        typeof globalThis.CSS !== "undefined" && "highlights" in globalThis.CSS
    );
}

/**
 * Collects Range objects covering content nodes that immediately precede
 * a citation marker element, stopping at the previous marker or the start
 * of the parent. Trims leading sentence-ending punctuation (e.g. ". ")
 * from the earliest node so the previous sentence's period isn't highlighted.
 */
function collectPrecedingRanges(marker: Element): Range[] {
    const parent = marker.parentNode;
    if (!parent) return [];

    const ranges: Range[] = [];
    let sibling: Node | null = marker.previousSibling;

    while (sibling) {
        if (
            sibling instanceof HTMLElement &&
            sibling.hasAttribute("data-citation-marker")
        ) {
            break;
        }

        if ((sibling.textContent || "").trim().length > 0) {
            const range = new Range();
            range.selectNodeContents(sibling);
            ranges.push(range);
        }

        sibling = sibling.previousSibling;
    }

    // The last range in the array is the earliest (furthest-back) node.
    // Trim any leading sentence-ending punctuation so e.g. ". Some text"
    // starts the highlight at "Some" instead of at the period.
    if (ranges.length > 0) {
        const earliest = ranges[ranges.length - 1];
        const node = earliest?.startContainer;

        if (node?.nodeType === Node.TEXT_NODE) {
            const text = node.textContent || "";
            const match = /^[.!?;:,\s]+/.exec(text);
            if (match) {
                earliest?.setStart(node, match[0].length);
            }
        }
    }

    return ranges;
}

const getRovoChatUrl = (query: string) => {
    const chatUrl = new URL("https://home.atlassian.com/chat");
    chatUrl.searchParams.set("rovoChatPathway", "chat");
    chatUrl.searchParams.set("rovoChatPrompt", query);

    const loginUrl = `https://id.atlassian.com/login?continue=${encodeURIComponent(
        chatUrl.toString()
    )}`;

    return loginUrl;
};

// Temporary Rovo logo to allow logo to avoid being filled with secondary button black
const RovoLogo = ({
    width = 20,
    height = 20
}: {
    width?: number;
    height?: number;
}) => (
    <svg
        width={width}
        height={height}
        viewBox="0 0 50 55"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M22.887 35.9136L6.40233 45.431L3.11886 43.5365C1.19061 42.4248 -0.00268555 40.3624 -0.00268555 38.1405V16.8804C-0.00268555 14.6497 1.18361 12.5963 3.11759 11.4852L16.9673 3.49463C16.9366 3.58087 16.9073 3.66769 16.8795 3.75505C16.6231 4.55062 16.4872 5.39119 16.4872 6.25036V27.5105C16.4872 30.4308 18.0428 33.1247 20.5779 34.5818L22.887 35.9136Z"
            style={{ fill: wacToken("color.primary.blue") }}
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M22.887 35.9136L6.40234 45.431L21.5432 54.1665C23.437 55.2582 25.7674 55.2776 27.6766 54.2245C27.7112 54.1977 27.748 54.173 27.787 54.1505C29.1474 53.3661 30.1423 52.1084 30.6101 50.6555C30.8005 50.0548 30.9011 49.4202 30.9011 48.7705V40.5353L22.887 35.9136Z"
            style={{ fill: wacToken("color.extended.lime600") }}
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M46.2189 11.4643L42.6873 9.42676L26.2026 18.9442L28.7608 20.4195C31.2963 21.8765 32.8509 24.5698 32.8509 27.4905V48.7506C32.8509 49.6109 32.7146 50.4526 32.4575 51.2492C32.43 51.3354 32.4011 51.4212 32.3708 51.5063L46.2205 43.5157C48.1545 42.4046 49.3407 40.3512 49.3407 38.1205V16.8604C49.3407 14.6385 48.1472 12.576 46.2189 11.4643Z"
            style={{ fill: wacToken("color.extended.purple600") }}
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M26.1971 18.9434L18.4312 14.4649V6.2296C18.4312 5.58068 18.5315 4.94676 18.7215 4.34668C19.1886 2.89435 20.1819 1.63706 21.5409 0.852193L21.545 0.849816C21.584 0.827339 21.621 0.802411 21.6556 0.775607C23.5649 -0.277534 25.8952 -0.258187 27.7891 0.83365L42.6818 9.42599L26.1971 18.9434Z"
            style={{ fill: wacToken("color.primary.saffron") }}
        />
    </svg>
);

const RovoButton = ({
    text,
    href,
    onClick
}: {
    text: string;
    href: string;
    onClick: () => void;
}) => (
    <Button
        variant="secondary"
        colorAgainst="white"
        css={rovoButtonStyle}
        href={href}
        onClick={onClick}
        target="_blank"
        rel="noopener noreferrer"
    >
        <RovoLogo width={20} height={20} />
        <span>{text}</span>
    </Button>
);

const ButtonComponent = ({
    text,
    buttonType,
    href
}: {
    text: string;
    buttonType: string;
    href: string;
}) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const handleClick = (ctaType: string) => {
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "searchAiSummaryCta",
            source: "searchAiSummary",
            attributes: {
                product: "search",
                text,
                href,
                ctaType
            }
        }).fire();
    };

    if (buttonType === "navigation") {
        return (
            <Link
                type="primary"
                aria-label={`${text}button`}
                text={text}
                href={href}
                onClick={() => handleClick("navigation")}
            />
        );
    }

    if (buttonType === "rovo") {
        return (
            <RovoButton
                text={text}
                href={href}
                onClick={() => handleClick("rovo")}
            />
        );
    }

    return (
        <Button
            variant="primary_alt"
            size="small"
            colorAgainst="white"
            href={href}
            onClick={() => handleClick("signup")}
            target="_blank"
            rel="noopener noreferrer"
        >
            {text}
        </Button>
    );
};

const createCtaButtonExtensionHandler = (
    isRovoEnabledCohort: boolean,
    query?: string
): ExtensionHandlers => ({
    "com.atlassian.wac": (ext) => {
        const { extensionKey, parameters } = ext;

        if (extensionKey === "cta-button" && parameters) {
            const { text, buttonType, href } = parameters as {
                text: string;
                buttonType: string;
                href: string;
            };

            const isRovoButton = buttonType === "rovo";
            const resolvedText =
                isRovoButton && isRovoEnabledCohort
                    ? "Send this to Rovo"
                    : text;
            const resolvedHref =
                isRovoButton && isRovoEnabledCohort
                    ? getRovoChatUrl(query || "")
                    : href;

            // Wrap in span to ensure inline rendering
            return (
                <div
                    css={[
                        buttonWrapperStyle,
                        isRovoButton && rovoButtonWrapperStyle
                    ]}
                >
                    <ButtonComponent
                        text={resolvedText}
                        buttonType={buttonType}
                        href={resolvedHref}
                    />
                </div>
            );
        }

        return null;
    }
});

// Extension handler for CTA buttons
// Handles inlineExtension nodes with extensionKey: "cta-button"
export const MarkdownRenderer = ({
    answerString,
    statusComponent,
    query,
    isSourceFiltered
}: MarkdownRendererProps) => {
    const { isRovoEnabledCohort } = useAISummaryContext();
    const containerRef = useRef<HTMLDivElement>(null);

    const docContent = useMemo(
        () => encodeAdf(answerString, false),
        [answerString]
    );

    const ctaButtonExtensionHandler = useMemo(
        () =>
            createCtaButtonExtensionHandler(
                Boolean(isRovoEnabledCohort),
                query
            ),
        [isRovoEnabledCohort, query]
    );

    // Wrap status component in a marker span so we can locate it in the DOM
    const wrappedStatusComponent = useCallback(
        (props: { text?: string; sources?: Source[] }) => (
            <span data-citation-marker="">{statusComponent(props)}</span>
        ),
        [statusComponent]
    );

    // Inject a ::highlight() style rule for the CSS Custom Highlight API
    useEffect(() => {
        if (!supportsHighlightAPI()) return;

        const style = document.createElement("style");
        style.textContent = `::highlight(${HIGHLIGHT_NAME}) { background-color: ${token(
            "color.background.accent.blue.subtlest"
        )}; }`;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    // Clear the highlight when the source filter is dismissed
    useEffect(() => {
        if (!isSourceFiltered && supportsHighlightAPI()) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ((CSS as any).highlights as Map<string, unknown>).delete(
                HIGHLIGHT_NAME
            );
        }
    }, [isSourceFiltered]);

    // Event delegation: clicking a citation highlights its preceding text
    // via the CSS Highlight API (no DOM mutation, so no React conflicts)
    useEffect(() => {
        const container = containerRef.current;
        if (!container || !supportsHighlightAPI()) return;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const highlights = (CSS as any).highlights as Map<string, unknown>;

        const handler = (e: Event) => {
            const target = e.target as HTMLElement;
            const marker = target.closest("[data-citation-marker]");

            if (!marker) return;

            // Clear previous highlight before applying new one
            highlights.delete(HIGHLIGHT_NAME);

            const ranges = collectPrecedingRanges(marker);

            if (ranges.length > 0) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
                const highlight = new ((globalThis as any).Highlight as new (
                    ...args: Range[]
                ) => unknown)(...ranges);
                highlights.set(HIGHLIGHT_NAME, highlight);
            }
        };

        container.addEventListener("click", handler);
        return () => container.removeEventListener("click", handler);
    }, []);

    const config: RendererProps = {
        // @ts-expect-error - DocNode type unavailable, fallback to unknown
        document: docContent as unknown,
        nodeComponents: {
            status: wrappedStatusComponent,
            media: MediaComponent,
            mediaSingle: MediaSingleComponent
        },
        extensionHandlers: ctaButtonExtensionHandler,
        smartLinks: {
            ssr: true
        }
    };

    return (
        <div ref={containerRef}>
            <Suspense fallback={<Fragment />}>
                <AkReactRenderer {...config} />
            </Suspense>
        </div>
    );
};
